import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
// import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { toast } from 'react-toastify';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

// Initialize Stripe
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const PlanCard = styled.div`
  background: ${({ theme, featured }) =>
    featured ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : theme.color.cardBackground};
  border-radius: 16px;
  padding: 30px;
  position: relative;
  box-shadow: ${({ featured }) =>
    featured ? '0 10px 40px rgba(102, 126, 234, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.1)'};
  transform: ${({ featured }) => featured ? 'scale(1.05)' : 'scale(1)'};
  transition: all 0.3s ease;
  border: ${({ featured }) => featured ? '2px solid #fff' : '1px solid rgba(255,255,255,0.1)'};
  
  &:hover {
    transform: ${({ featured }) => featured ? 'scale(1.08)' : 'scale(1.02)'};
    box-shadow: ${({ featured }) =>
    featured ? '0 15px 50px rgba(102, 126, 234, 0.5)' : '0 8px 30px rgba(0, 0, 0, 0.15)'};
  }
`;

const Badge = styled.div`
  position: absolute;
  top: -10px;
  right: 20px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 10px rgba(245, 87, 108, 0.3);
`;

const PlanName = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: ${({ featured }) => featured ? '#fff' : ({ theme }) => theme.color.primaryText};
  margin-bottom: 10px;
`;

const PlanDescription = styled.p`
  font-size: 14px;
  color: ${({ featured }) => featured ? 'rgba(255,255,255,0.9)' : ({ theme }) => theme.color.secondaryText};
  margin-bottom: 20px;
  min-height: 40px;
`;

const GCoinsAmount = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: ${({ featured }) => featured ? '#fff' : '#667eea'};
  margin: 20px 0;
  display: flex;
  align-items: baseline;
  justify-content: center;
  
  span {
    font-size: 20px;
    margin-left: 8px;
    opacity: 0.8;
  }
`;

const BonusTag = styled.div`
  display: inline-block;
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 15px;
`;

const PriceTag = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: ${({ featured }) => featured ? '#fff' : ({ theme }) => theme.color.primaryText};
  margin: 20px 0;
  
  span {
    font-size: 18px;
    opacity: 0.7;
  }
`;

const BuyButton = styled.button`
  width: 100%;
  padding: 15px;
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  background: ${({ featured }) =>
    featured ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  
  div {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(102, 126, 234, 0.2);
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  margin: 20px 0;
`;

const InfoSection = styled.div`
  background: ${({ theme }) => theme.color.cardBackground};
  border-radius: 16px;
  padding: 30px;
  margin-top: 40px;
  border: 1px solid rgba(255,255,255,0.1);
`;

const InfoTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
  margin-bottom: 15px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.color.secondaryText};
  line-height: 1.6;
  margin-bottom: 10px;
`;

const SecurityBadges = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
  flex-wrap: wrap;
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 20px;
  font-size: 12px;
  color: #22c55e;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Main Component
const GCoinStore = ({ currentUser }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ENV_CONFIG.WALLET_BALANCE_ENDPOINT.replace('/balance', '/plans')}`);

      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(t('gcoinStore.errorLoadingPlans', 'Impossible de charger les plans. Veuillez réessayer.'));
      toast.error(t('gcoinStore.toastLoadingPlansError', 'Erreur lors du chargement des plans'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handlePurchase = async (planId) => {
    if (!currentUser) {
      toast.error(t('gcoinStore.loginRequired', 'Veuillez vous connecter pour acheter des G-Coins'));
      window.location.href = '/?noAuth';
      return;
    }

    setSelectedPlanId(planId);
    setPurchasing(true);
    setError(null);

    try {
      // Create Stripe checkout session
      const response = await axios.post(
        `${ENV_CONFIG.WALLET_BALANCE_ENDPOINT.replace('/balance', '/create-checkout-session')}`,
        {
          planId: planId.toString(),
          userId: currentUser.id.toString()
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.success && response.data.url) {
        // Redirect to Stripe Checkout
        // const stripe = await stripePromise;

        // Option 1: Direct URL redirect (simpler)
        window.location.href = response.data.url;

        // Option 2: Using Stripe's redirect method (alternative)
        // const { error } = await stripe.redirectToCheckout({
        //   sessionId: response.data.sessionId
        // });
        // 
        // if (error) {
        //   toast.error('Erreur de paiement: ' + error.message);
        //   setPurchasing(false);
        // }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.response?.data?.message || t('gcoinStore.paymentInitFailed', "Échec de l'initialisation du paiement"));
      toast.error(t('gcoinStore.startPaymentFailed', 'Impossible de démarrer le paiement'));
      setPurchasing(false);
      setSelectedPlanId(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div></div>
        </LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>💰 {t('gcoinStore.title', 'Boutique G-Coins')}</Title>
        <Subtitle>{t('gcoinStore.subtitle', 'Achetez des G-Coins pour débloquer toutes les fonctionnalités de CoreArena')}</Subtitle>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <PlansGrid>
        {plans.map((plan) => (
          <PlanCard key={plan.id} featured={plan.is_featured}>
            {plan.badge_text && <Badge>{plan.badge_text}</Badge>}

            <PlanName featured={plan.is_featured}>{plan.plan_name}</PlanName>

            <PlanDescription featured={plan.is_featured}>
              {plan.description || `${t('gcoinStore.packOf', 'Pack de')} ${plan.total_gcoins} G-Coins`}
            </PlanDescription>

            <GCoinsAmount featured={plan.is_featured}>
              {plan.total_gcoins.toLocaleString()}
              <span>G-Coins</span>
            </GCoinsAmount>

            {plan.bonus_gcoins > 0 && (
              <BonusTag>
                🎁 +{plan.bonus_gcoins} {t('gcoinStore.bonus', 'bonus')}
              </BonusTag>
            )}

            <PriceTag featured={plan.is_featured}>
              {plan.price_eur.toFixed(2)} <span>€</span>
            </PriceTag>

            <BuyButton
              featured={plan.is_featured}
              onClick={() => handlePurchase(plan.id)}
              disabled={purchasing}
            >
              {purchasing && selectedPlanId === plan.id ? t('gcoinStore.loading', 'Chargement...') : t('gcoinStore.buy', 'Acheter')}
            </BuyButton>
          </PlanCard>
        ))}
      </PlansGrid>

      <InfoSection>
        <InfoTitle>ℹ️ {t('gcoinStore.aboutTitle', 'À propos des G-Coins')}</InfoTitle>
        <InfoText>
          {t('gcoinStore.aboutIntro', 'Les G-Coins sont la monnaie virtuelle de CoreArena. Utilisez-les pour :')}
        </InfoText>
        <InfoText>• 📺 {t('gcoinStore.aboutItemStreams', 'Créer des streams et événements')}</InfoText>
        <InfoText>• 🎟️ {t('gcoinStore.aboutItemTickets', 'Acheter des tickets pour des événements')}</InfoText>
        <InfoText>• 🏆 {t('gcoinStore.aboutItemTournaments', 'Participer à des tournois')}</InfoText>
        <InfoText>• ⭐ {t('gcoinStore.aboutItemPremium', 'Accéder à des fonctionnalités premium')}</InfoText>
      </InfoSection>

      <SecurityBadges>
        <SecurityBadge>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {t('gcoinStore.sslSecurePayment', 'Paiement sécurisé SSL')}
        </SecurityBadge>
        <SecurityBadge>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
          </svg>
          Powered by Stripe
        </SecurityBadge>
        <SecurityBadge>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {t('gcoinStore.moneyBackGuarantee', 'Remboursement garanti')}
        </SecurityBadge>
      </SecurityBadges>
    </PageContainer>
  );
};

export default GCoinStore;



